import { Component, EventEmitter, Input, OnInit, Output } from "@angular/core";
import { AlertController, ModalController, ToastController } from "@ionic/angular";
import { differenceBy, flatMap } from "lodash";
import { SelectItem } from "src/app/shared/classes/SelectItem";
import { QuestionConfigs } from "src/app/shared/configurations/question-configuration";
import {
    QuestionConfig,
    QuestionConfigTypeEnum,
    QuestionFormulaType,
    QuestionTypeConfig,
    QuestionTypeEnum,
    QuestionViewType,
    RelatedQuestion,
} from "src/app/shared/interfaces/process-config.interface";
import { ProcessTypeEnum } from "src/app/shared/interfaces/process.interface";
import { TranslationService } from "src/app/shared/services/translation.service";
import { BaseClass } from "src/app/shared/classes/base";
import { ConfigurationBooleanValuesPage } from "./boolean-values/configuration-boolean-values.page";
import { QuestionAttributesComponent } from "./question-attributes/question-attributes.component";
import { ConfigurationValuesRulingPage } from "./values-ruling/configuration-values-ruling.page";
import { Version } from "src/app/processes/_interfaces/version.interface";
import { ConfigService } from "src/app/shared/services/multi-clients/config.service";

@Component({
    selector: "app-question-configuration",
    templateUrl: "./question-configuration.component.html",
    styleUrls: ["../../manage-version.page.scss"],
})
export class QuestionConfiguration extends BaseClass implements OnInit {
    @Input() isManagerQuestion: boolean;
    @Input() version: Version;
    @Input() question: QuestionConfig;
    @Input() questionTypeConfigs: QuestionTypeConfig[] = [];
    @Input() language: string;
    @Input() groupsSelectItems: SelectItem[];
    @Output() deleteQuestion: EventEmitter<string> = new EventEmitter();
    @Output() reorderQuestions: EventEmitter<string> = new EventEmitter();
    @Output() changeGroup: EventEmitter<{ questionId: string; newGroupId?: number }> = new EventEmitter();
    @Output() updateQuestionTypeConfig: EventEmitter<QuestionTypeConfig> = new EventEmitter();

    public questionsTypeAvailable: any;
    public potentialTypeConfigEnabled:boolean;
    public hidden: boolean = true;
    public characterCount: Array<number> = [];

    public relatedQuestions: QuestionConfig[] = [];
    public QuestionType = QuestionTypeEnum;
    public QuestionFormulaType = QuestionFormulaType;
    public QuestionConfigType = QuestionConfigTypeEnum;
    public ProcessType = ProcessTypeEnum;
    public ionSelectTextAreaMaxLenght = QuestionConfigs.SelectionCharLimit.toString();
    public hasAttributesConfig = false;
    public relatedQuestionsMappedById: { [key: string]: QuestionConfig };
    public selectedRelatedQuestions: Array<RelatedQuestion>;

    constructor(
        protected _toastController: ToastController,
        protected _translationService: TranslationService,
        private _alertController: AlertController,
        private _modalController: ModalController,
        private _configService: ConfigService
    ) {
        super(_toastController, _translationService);
    }

    ngOnInit() {
        this.questionsTypeAvailable = this._configService.settings.features.questionTypeAvailability.question;
        this.potentialTypeConfigEnabled = this._configService.settings.features.potentialTypeConfigEnabled;

        this.hasAttributesConfig =
            this.version &&
            (this.version.type === ProcessTypeEnum.SelfEvaluation ||
                this.version.type === ProcessTypeEnum.ManagerEvaluation ||
                this.version.type === ProcessTypeEnum.TwoPlusTwoCoaching ||
                this.version.type === ProcessTypeEnum.ThreeSixtyEvaluation ||
                this.version.type === ProcessTypeEnum.FreeEvaluationEndYear ||
                this.version.type === ProcessTypeEnum.FreeEvaluationMidYear ||
                this.version.type === ProcessTypeEnum.FinalFeedback);

        const managerQuestions = flatMap(this.version.managerQuestionGroups, (qg) => qg.questions).filter(
            (q) =>
                [QuestionTypeEnum.Selection, QuestionTypeEnum.Number, QuestionTypeEnum.Slider].includes(q.typeConfig.type) &&
                q.typeConfig.possibleValues.every((pv) => !isNaN(parseFloat(pv))),
        );

        this.relatedQuestions = this.isManagerQuestion ? managerQuestions : differenceBy(this.version.relatedQuestions, managerQuestions, (q) => q.id);
        this.relatedQuestionsMappedById = this.relatedQuestions.reduce((acc, relatedQuestion) => {
            acc[relatedQuestion.id] = relatedQuestion;
            return acc;
        }, {});
    }

    public changeOrder(questionId: string, newOrder: number): void {
        this.question.order = newOrder;
        this.reorderQuestions.emit(questionId);
    }

    public clearOnTypeChange(): void {
        if (this.question.typeConfig) {
            this.question.typeConfig = this.createNewTypeConfig(this.question.typeConfig.type, null);
        }
    }

    public clearOnViewTypeChange($event: any): void {
        let viewType = $event.target.value;
        if (this.question) {
            this.question.viewType = viewType;
        }
    }

    public changeSelectedLanguage(language: "pt-BR" | "en-US" | "es-ES"): void {
        this.language = language;
    }

    public isValidAmount(amount: string | number): number | null {
        if (amount === undefined || amount === null) return null;

        const parsedAmount = parseInt(amount as string, 10);
        return isNaN(parsedAmount) || parsedAmount < 2 || parsedAmount > 6 ? null : parsedAmount;
    }

    public async confirmDeleteQuestion(questionId: string): Promise<void> {
        const alert = await this._alertController.create({
            message: this._translationService.getTranslation("manageVersion.confirmDeleteQuestion"),
            buttons: [
                {
                    text: this._translationService.getTranslation("generic.no"),
                    role: "cancel",
                    cssClass: "danger",
                },
                {
                    text: this._translationService.getTranslation("generic.yes"),
                    handler: () => {
                        this.deleteQuestion.emit(questionId);
                    },
                },
            ],
        });

        await alert.present();
    }

    public async configureBooleanValues(typeConfig: QuestionTypeConfig) {
        const modal = await this._modalController.create({
            component: ConfigurationBooleanValuesPage,
            cssClass: "view-goals-modal-style boolean",
            componentProps: {
                language: this.language,
                typeConfig: typeConfig,
            },
        });

        modal.onDidDismiss().then((data) => {
            this.addOrUpdateTypeConfigs(typeConfig);
        });

        return await modal.present();
    }

    public async configureValuesRule(typeConfig: QuestionTypeConfig) {
        const modal = await this._modalController.create({
            component: ConfigurationValuesRulingPage,
            cssClass: "view-goals-modal-style ruling",
            componentProps: {
                language: this.language,
                typeConfig: typeConfig,
            },
        });

        modal.onDidDismiss().then((data) => {
            this.addOrUpdateTypeConfigs(typeConfig);
        });

        return await modal.present();
    }

    public addOrUpdateTypeConfigs(typeConfig: QuestionTypeConfig) {
        const index = this.questionTypeConfigs.findIndex((x) => x.id === typeConfig.id && x.type === typeConfig.type);
        if (index < 0) {
            this.questionTypeConfigs.push(typeConfig);
        } else {
            this.questionTypeConfigs[index] = typeConfig;
            this.updateQuestionTypeConfig.emit(typeConfig);
        }
    }

    public addPossibleValue(question: QuestionConfig): void {
        question.typeConfig.possibleValuesGlobalized["en-US"].push("");
        question.typeConfig.possibleValuesGlobalized["es-ES"].push("");
        question.typeConfig.possibleValuesGlobalized["pt-BR"].push("");
    }

    public removePossibleValue(question: QuestionConfig, index: number): void {
        question.typeConfig.possibleValuesGlobalized["en-US"].splice(index, 1);
        question.typeConfig.possibleValuesGlobalized["es-ES"].splice(index, 1);
        question.typeConfig.possibleValuesGlobalized["pt-BR"].splice(index, 1);
    }

    public valueTrackBy(index: number, obj) {
        return index;
    }

    public groupChanged(event) {
        const group = this.groupsSelectItems.find((x) => x.id === event.detail.value);
        this.changeGroup.emit({ questionId: this.question.guid, newGroupId: event.detail.value });
    }

    public toggleExpand() {
        this.question["expanded"] = !this.question["expanded"];
    }

    public clearQuestionType() {
        this.question.type = null;
    }

    public onChangeQuestionTypeConfig(event: any, type: QuestionTypeEnum) {
        const value = event.detail.value;
        const config = this.questionTypeConfigs.find((x) => x.id === value && x.type === type);
        if (config) {
            this.question.typeConfig = config;
            if (config.possibleValues) this.question.typeConfig.valuesAmount = config.possibleValues.length;
        } else {
            this.question.typeConfig = this.createNewTypeConfig(type, null);
        }
    }

    public createNewTypeConfig(type: QuestionTypeEnum, valuesAmount: number): QuestionTypeConfig {
        const config = <QuestionTypeConfig>{
            id: 0,
            name: "",
            type: type,
            possibleValues: [],
            possibleValuesGlobalized: { "en-US": [], "es-ES": [], "pt-BR": [] },
        };
        config.valuesAmount = valuesAmount;
        return config;
    }

    public async manageAttributes() {
        const modal = await this._modalController.create({
            component: QuestionAttributesComponent,
            // cssClass: '',
            componentProps: {
                language: this.language,
                typeConfig: null,
                question: this.question,
            },
        });

        return await modal.present();
    }

    removeRelatedQuestion(relatedQuestionId: number) {
        const index = this.question.relatedQuestions.findIndex((relatedQuestion) => relatedQuestion.questionId === relatedQuestionId);
        if (index >= 0) {
            this.question.relatedQuestions.splice(index, 1);
        }
        const indexRelated = this.selectedRelatedQuestions.findIndex((relatedQuestion) => relatedQuestion.questionId === relatedQuestionId);
        if (indexRelated >= 0) {
            this.selectedRelatedQuestions.splice(indexRelated, 1);
            this.selectedRelatedQuestions = [...this.selectedRelatedQuestions];
        }
    }

    async handleSelectedRelatedQuestion() {
        if (this.selectedRelatedQuestions === undefined) return;
        const alreadyHasAllRelatedQuestions = this.selectedRelatedQuestions.every((relatedQuestion) => this.question.relatedQuestions.includes(relatedQuestion));
        if (this.question.relatedQuestions && this.question.relatedQuestions.length > 0 && alreadyHasAllRelatedQuestions) {
            await this.notify(this._translationService.getTranslation("manageVersion.relatedQuestionAlreadyAdded"), "warning");
            return;
        }
        const relatedQuestions = this.selectedRelatedQuestions.filter((relatedQuestion) => !this.question.relatedQuestions.includes(relatedQuestion));
        relatedQuestions.forEach((relatedQuestion) => {
            this.question.relatedQuestions.push(relatedQuestion);
        });
        // this.selectedRelatedQuestions = undefined;
    }

    alreadyHasRelatedQuestion(relatedQuestion: QuestionConfig) {
        var alreadySelected = this.question.relatedQuestions.some((question) => question.questionId === relatedQuestion.id);
        return alreadySelected;
    }

    protected readonly QuestionViewType = QuestionViewType;
}
